import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';

/**
 * Auvik-fed surfaces (devices, interfaces, alerts). Auvik is not connected
 * in production today, so every consumer must treat "no rows" as
 * "Auvik not connected" rather than "all clear".
 */

export const LOCATION_DEVICE_COLUMNS =
  'id, name, device_type, status, ip_address, mac_address, firmware_version, software_version, vendor_name, make_model, serial_number, last_seen_at, availability_pct, availability_from, availability_to, updated_at';

export type LocationDevice = {
  id: string;
  name: string | null;
  device_type: string | null;
  status: string | null;
  ip_address: string | null;
  mac_address: string | null;
  firmware_version: string | null;
  software_version: string | null;
  vendor_name: string | null;
  make_model: string | null;
  serial_number: string | null;
  last_seen_at: string | null;
  availability_pct: number | null;
  availability_from: string | null;
  availability_to: string | null;
  updated_at: string | null;
};

export function useLocationDevices(locationId: string | undefined) {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['location-devices', scope.key, locationId ?? ''],
    enabled: Boolean(locationId) && scope.ready && scope.ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('devices_customer_view')
        .select(LOCATION_DEVICE_COLUMNS)
        .eq('location_id', locationId!)
        .in('customer_id', scope.ids)
        .order('name');
      if (error) throw error;
      return (data ?? []) as LocationDevice[];
    },
    staleTime: 2 * 60 * 1000,
  });
}

export interface LocationNetworkLive {
  alerts: Array<{
    id: string | null;
    severity: string | null;
    status: string | null;
    alert_name: string | null;
    message: string | null;
    detected_on: string | null;
    entity_name: string | null;
    device_id: string | null;
  }>;
  interfaces: Array<{
    id: string | null;
    device_id: string | null;
    name: string | null;
    interface_type: string | null;
    operational_status: string | null;
    admin_status: boolean | null;
    ip_address: string | null;
    negotiated_speed: string | null;
    duplex: string | null;
  }>;
  wlans: Array<{
    id: string | null;
    name: string | null;
    enabled: boolean | null;
    security: string | null;
    hidden: boolean | null;
    updated_at: string | null;
  }>;
  clients: Array<{
    id: string | null;
    name: string | null;
    ip_address: string | null;
    mac_address: string | null;
    connection_type: string | null;
    ssid: string | null;
    connected_at: string | null;
    updated_at: string | null;
  }>;
  accessPoints: Array<{
    id: string | null;
    device_id: string | null;
    name: string | null;
    model: string | null;
    mac_address: string | null;
    ip_address: string | null;
    firmware_version: string | null;
    updated_at: string | null;
  }>;
}

export function useLocationNetworkLive(locationId: string | undefined) {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['location-network-live', scope.key, locationId ?? ''],
    enabled: Boolean(locationId) && scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<LocationNetworkLive> => {
      const ids = scope.ids;
      const [alerts, interfaces, wlans, clients, accessPoints] = await Promise.all([
        supabase
          .from('network_alerts_customer_view')
          .select('id, severity, status, alert_name, message, detected_on, entity_name, device_id')
          .eq('location_id', locationId!)
          .in('customer_id', ids)
          .or('status.is.null,and(status.neq.cleared,status.neq.resolved)')
          .order('detected_on', { ascending: false })
          .limit(25),
        supabase
          .from('device_interfaces_customer_view')
          .select('id, device_id, name, interface_type, operational_status, admin_status, ip_address, negotiated_speed, duplex')
          .eq('location_id', locationId!)
          .in('customer_id', ids)
          .order('name')
          .limit(80),
        supabase
          .from('unifi_wlans_customer_view')
          .select('id, name, enabled, security, hidden, updated_at')
          .eq('location_id', locationId!)
          .in('customer_id', ids)
          .order('name'),
        supabase
          .from('unifi_clients_customer_view')
          .select('id, name, ip_address, mac_address, connection_type, ssid, connected_at, updated_at')
          .eq('location_id', locationId!)
          .in('customer_id', ids)
          .gte('updated_at', new Date(Date.now() - 15 * 60_000).toISOString())
          .order('name'),
        supabase
          .from('unifi_access_points_customer_view')
          .select('id, device_id, name, model, mac_address, ip_address, firmware_version, updated_at')
          .eq('location_id', locationId!)
          .in('customer_id', ids)
          .order('name'),
      ]);
      if (alerts.error) throw alerts.error;
      if (interfaces.error) throw interfaces.error;
      if (wlans.error) throw wlans.error;
      if (clients.error) throw clients.error;
      if (accessPoints.error) throw accessPoints.error;
      return {
        alerts: alerts.data ?? [],
        interfaces: interfaces.data ?? [],
        wlans: wlans.data ?? [],
        clients: clients.data ?? [],
        accessPoints: accessPoints.data ?? [],
      };
    },
    staleTime: 2 * 60 * 1000,
    refetchInterval: 60_000,
  });
}

export interface OpenNetworkAlert {
  id: string | null;
  location_id: string | null;
  alert_name: string | null;
  severity: string | null;
  status: string | null;
  entity_name: string | null;
  detected_on: string | null;
}

/**
 * Open Auvik alerts across the account scope. `connected` is false when the
 * scope has no Auvik devices at all — the dashboard then says "Auvik not
 * connected" instead of showing a reassuring zero.
 */
/** auvik-poll-devices runs every 5 minutes; allow a miss before saying stale. */
export const DEVICE_POLL_EXPECTED_REFRESH_MS = 15 * 60 * 1000;
export const DEVICE_POLL_STALE_MS = 60 * 60 * 1000;

export function useOpenNetworkAlerts() {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['open-network-alerts', scope.key],
    enabled: scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<{
      alerts: OpenNetworkAlert[];
      connected: boolean;
      lastPolledAt: string | null;
    }> => {
      const [alerts, devices, newestDevice] = await Promise.all([
        supabase
          .from('network_alerts_customer_view')
          .select('id, location_id, alert_name, severity, status, entity_name, detected_on')
          .in('customer_id', scope.ids)
          .or('status.is.null,and(status.neq.cleared,status.neq.resolved)')
          .order('detected_on', { ascending: false })
          .limit(50),
        supabase
          .from('devices_customer_view')
          .select('id', { count: 'exact', head: true })
          .in('customer_id', scope.ids),
        // An empty alert list is not evidence of a healthy network — it is only
        // meaningful if the poll that fills it is still running. The newest
        // device row is that poll's heartbeat.
        supabase
          .from('devices_customer_view')
          .select('updated_at')
          .in('customer_id', scope.ids)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      if (alerts.error) throw alerts.error;
      if (devices.error) throw devices.error;
      return {
        alerts: (alerts.data ?? []) as OpenNetworkAlert[],
        connected: (devices.count ?? 0) > 0,
        lastPolledAt: newestDevice.data?.updated_at ?? null,
      };
    },
    staleTime: 30 * 1000,
  });
}

export function formatAvailabilityPct(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  return `${Number(value).toFixed(Number(value) % 1 === 0 ? 0 : 1)}%`;
}
